/**
 * CareerVisionPage — Guided interview for capturing career goals and values
 * Includes coaching hints, "Talk with Alex" voice assist per section,
 * and a final "My Profile" step with LLM-generated summary.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  saveCareerVision,
  getCareerVision,
  extractCareerVisionFromTranscript,
  generateCareerProfile,
  CareerVisionData,
  StepKey,
} from '../../services/careerVision';
import { VoiceMode } from '../../components/Chat/VoiceMode';
import { TranscriptEntry } from '../../hooks/useGeminiLive';

const VISION_STEPS: StepKey[] = ['shortTermGoals', 'midTermGoals', 'longTermGoals', 'values', 'likes', 'dislikes', 'idealRole'];
const TOTAL_STEPS = VISION_STEPS.length + 1; // +1 for "My Profile"
const PROFILE_STEP = VISION_STEPS.length; // index 7

const TAG_STEPS: StepKey[] = ['values', 'likes', 'dislikes'];

/** Map step keys to locale hint keys */
const HINT_KEYS: Record<StepKey, string> = {
  shortTermGoals: 'careerVision.shortTermHint',
  midTermGoals: 'careerVision.midTermHint',
  longTermGoals: 'careerVision.longTermHint',
  values: 'careerVision.valuesHint',
  likes: 'careerVision.likesHint',
  dislikes: 'careerVision.dislikesHint',
  idealRole: 'careerVision.idealRoleHint',
};

/** Section-specific system prompts for voice assist */
const VOICE_PROMPTS: Record<StepKey, { en: string; fr: string }> = {
  shortTermGoals: {
    en: 'You are a career coach helping the user define their short-term career goals (1-2 years). Ask about skills they want to develop, positions they want to aim for, projects they want to complete, and milestones they want to hit. Keep the conversation focused and brief (3-5 minutes). Ask one question at a time.',
    fr: 'Vous êtes un coach de carrière aidant l\'utilisateur à définir ses objectifs de carrière à court terme (1-2 ans). Posez des questions sur les compétences qu\'il veut développer, les postes qu\'il vise, les projets qu\'il veut accomplir. Gardez la conversation ciblée et brève (3-5 minutes). Posez une question à la fois. Répondez en français.',
  },
  midTermGoals: {
    en: 'You are a career coach helping the user envision where they want to be in 5 years. Ask about the type of role, company size, industry, level of responsibility, or impact they want to have. Keep it focused (3-5 minutes). Ask one question at a time.',
    fr: 'Vous êtes un coach de carrière aidant l\'utilisateur à envisager où il veut être dans 5 ans. Posez des questions sur le type de rôle, la taille de l\'entreprise, le secteur ou l\'impact souhaité. Gardez la conversation ciblée (3-5 minutes). Posez une question à la fois. Répondez en français.',
  },
  longTermGoals: {
    en: 'You are a career coach helping the user think big about their 10-15 year career vision. Ask about legacy, leadership aspirations, entrepreneurship, expertise areas, or the mark they want to leave on their industry. Keep it focused (3-5 minutes). Ask one question at a time.',
    fr: 'Vous êtes un coach de carrière aidant l\'utilisateur à penser grand pour sa vision de carrière à 10-15 ans. Posez des questions sur l\'héritage, les aspirations de leadership, l\'entrepreneuriat ou la marque qu\'il veut laisser. Gardez la conversation ciblée (3-5 minutes). Posez une question à la fois. Répondez en français.',
  },
  values: {
    en: 'You are a career coach helping the user identify their core career values. Ask what matters most to them at work — autonomy, impact, learning, stability, creativity, collaboration, recognition, work-life balance, etc. Help them prioritize. Keep it focused (3-5 minutes). Ask one question at a time.',
    fr: 'Vous êtes un coach de carrière aidant l\'utilisateur à identifier ses valeurs fondamentales. Demandez ce qui compte le plus au travail — autonomie, impact, apprentissage, stabilité, créativité, etc. Aidez-le à prioriser. Gardez la conversation ciblée (3-5 minutes). Posez une question à la fois. Répondez en français.',
  },
  likes: {
    en: 'You are a career coach helping the user identify what energizes them at work. Ask about tasks, environments, team dynamics, project types, and activities they enjoy most. What makes them lose track of time? Keep it focused (3-5 minutes). Ask one question at a time.',
    fr: 'Vous êtes un coach de carrière aidant l\'utilisateur à identifier ce qui l\'énergise au travail. Posez des questions sur les tâches, environnements, dynamiques d\'équipe et activités qu\'il apprécie le plus. Gardez la conversation ciblée (3-5 minutes). Posez une question à la fois. Répondez en français.',
  },
  dislikes: {
    en: 'You are a career coach helping the user identify what drains them at work. Ask about frustrations, environments, management styles, and types of work they want to avoid. What saps their motivation? Keep it focused (3-5 minutes). Ask one question at a time.',
    fr: 'Vous êtes un coach de carrière aidant l\'utilisateur à identifier ce qui l\'épuise au travail. Posez des questions sur les frustrations, environnements, styles de management et types de travail qu\'il veut éviter. Gardez la conversation ciblée (3-5 minutes). Posez une question à la fois. Répondez en français.',
  },
  idealRole: {
    en: 'You are a career coach helping the user describe their dream role. Ask about day-to-day activities, team size, industry, company culture, level of autonomy, and what success looks like in that role. Keep it focused (3-5 minutes). Ask one question at a time.',
    fr: 'Vous êtes un coach de carrière aidant l\'utilisateur à décrire son rôle de rêve. Posez des questions sur les activités quotidiennes, la taille de l\'équipe, le secteur, la culture d\'entreprise et ce à quoi ressemble le succès. Gardez la conversation ciblée (3-5 minutes). Posez une question à la fois. Répondez en français.',
  },
};

export function CareerVisionPage() {
  const { t, language } = useLanguage();
  const [step, setStep] = useState(0);
  const [vision, setVision] = useState<CareerVisionData>({
    shortTermGoals: '',
    midTermGoals: '',
    longTermGoals: '',
    values: [],
    likes: [],
    dislikes: [],
    idealRole: '',
  });
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [voiceActive, setVoiceActive] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [generatingProfile, setGeneratingProfile] = useState(false);

  const loadVision = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCareerVision();
      setVision(data);
    } catch (err: any) {
      console.error('Failed to load career vision:', err);
      setError(t('careerVision.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadVision();
  }, [loadVision]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSaved(false);
      await saveCareerVision(vision);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      console.error('Failed to save career vision:', err);
      setError(t('careerVision.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const isProfileStep = step === PROFILE_STEP;
  const currentKey = isProfileStep ? null : VISION_STEPS[step];
  const isTagStep = currentKey ? TAG_STEPS.includes(currentKey) : false;
  const isLastStep = step === TOTAL_STEPS - 1; // profile is the last step

  const addTag = () => {
    const val = tagInput.trim();
    if (!val || !isTagStep || !currentKey) return;
    const arr = vision[currentKey] as string[];
    if (!arr.includes(val)) {
      setVision({ ...vision, [currentKey]: [...arr, val] });
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    if (!isTagStep || !currentKey) return;
    const arr = vision[currentKey] as string[];
    setVision({ ...vision, [currentKey]: arr.filter(v => v !== tag) });
  };

  const handleVoiceClose = useCallback(async (transcript: TranscriptEntry[]) => {
    setVoiceActive(false);

    if (transcript.length === 0 || !currentKey) return;

    // Build transcript text
    const transcriptText = transcript
      .map(e => `${e.role === 'user' ? 'User' : 'Coach'}: ${e.text}`)
      .join('\n');

    try {
      setExtracting(true);
      setError('');
      const result = await extractCareerVisionFromTranscript(transcriptText, currentKey, language);

      if (result.items && TAG_STEPS.includes(currentKey)) {
        const existing = vision[currentKey] as string[];
        const merged = [...existing];
        for (const item of result.items) {
          if (!merged.includes(item)) {
            merged.push(item);
          }
        }
        setVision(prev => ({ ...prev, [currentKey]: merged }));
      } else if (result.content) {
        const existing = vision[currentKey] as string;
        const newContent = existing ? `${existing}\n\n${result.content}` : result.content;
        setVision(prev => ({ ...prev, [currentKey]: newContent }));
      }
    } catch (err: any) {
      console.error('Failed to extract from transcript:', err);
      setError(t('careerVision.extractError'));
    } finally {
      setExtracting(false);
    }
  }, [currentKey, language, vision, t]);

  const handleGenerateProfile = async () => {
    try {
      setGeneratingProfile(true);
      setError('');
      const profileSummary = await generateCareerProfile(vision, language);
      setVision(prev => ({ ...prev, profileSummary }));
    } catch (err: any) {
      console.error('Failed to generate profile:', err);
      setError(t('careerVision.profileError'));
    } finally {
      setGeneratingProfile(false);
    }
  };

  const hasAnyContent = vision.shortTermGoals || vision.midTermGoals || vision.longTermGoals ||
    vision.values.length > 0 || vision.likes.length > 0 || vision.dislikes.length > 0 || vision.idealRole;

  if (loading) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
        Loading...
      </div>
    );
  }

  // Pass section-specific coaching context as chat history for voice mode
  const lang = language === 'fr' ? 'fr' : 'en';
  const voiceChatHistory = currentKey ? [
    { role: 'user' as const, content: VOICE_PROMPTS[currentKey][lang] },
    { role: 'assistant' as const, content: lang === 'fr' ? 'Compris, je vais vous guider.' : 'Got it, I\'ll guide you through this.' },
  ] : [];

  return (
    <div style={{ padding: '20px', maxWidth: '700px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '4px' }}>{t('careerVision.title')}</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '24px' }}>
        {t('careerVision.subtitle')}
      </p>

      {/* Progress bar */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px' }}>
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
          <div
            key={i}
            onClick={() => setStep(i)}
            style={{
              flex: 1,
              height: '4px',
              borderRadius: '2px',
              backgroundColor: i <= step ? 'var(--primary)' : 'var(--border)',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
            }}
          />
        ))}
      </div>

      {/* Step indicator */}
      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '16px' }}>
        {t('careerVision.section')} {step + 1} {t('careerVision.stepOf')} {TOTAL_STEPS}
      </div>

      {isProfileStep ? (
        /* ===== MY PROFILE STEP ===== */
        <div>
          <h3 style={{ marginBottom: '8px', fontSize: '18px' }}>
            {t('careerVision.myProfile')}
          </h3>

          <p style={{
            fontSize: '13px',
            color: 'var(--text-muted)',
            marginBottom: '16px',
            lineHeight: '1.5',
            fontStyle: 'italic',
            padding: '10px 14px',
            backgroundColor: 'var(--surface-muted, rgba(79,70,229,0.05))',
            borderRadius: '8px',
            borderLeft: '3px solid var(--primary)',
          }}>
            {t('careerVision.myProfileHint')}
          </p>

          {vision.profileSummary ? (
            <div style={{
              padding: '16px',
              backgroundColor: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              fontSize: '14px',
              lineHeight: '1.7',
              whiteSpace: 'pre-wrap',
              color: 'var(--text)',
              marginBottom: '12px',
            }}>
              {vision.profileSummary}
            </div>
          ) : (
            <div style={{
              padding: '24px',
              backgroundColor: 'var(--surface)',
              border: '1px dashed var(--border)',
              borderRadius: '8px',
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: '14px',
              marginBottom: '12px',
            }}>
              {hasAnyContent
                ? t('careerVision.myProfileHint')
                : t('careerVision.profileEmpty')}
            </div>
          )}

          <button
            onClick={handleGenerateProfile}
            disabled={generatingProfile || !hasAnyContent}
            style={{
              padding: '10px 20px',
              backgroundColor: hasAnyContent ? 'var(--primary)' : 'var(--border)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: (generatingProfile || !hasAnyContent) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: (generatingProfile || !hasAnyContent) ? 0.6 : 1,
            }}
          >
            {generatingProfile ? t('careerVision.generatingProfile') : t('careerVision.generateProfile')}
          </button>
        </div>
      ) : currentKey && (
        /* ===== REGULAR VISION STEPS ===== */
        <div>
          {/* Section label */}
          <h3 style={{ marginBottom: '8px', fontSize: '18px' }}>
            {t(`careerVision.${currentKey}` as any)}
          </h3>

          {/* Coaching hint */}
          <p style={{
            fontSize: '13px',
            color: 'var(--text-muted)',
            marginBottom: '16px',
            lineHeight: '1.5',
            fontStyle: 'italic',
            padding: '10px 14px',
            backgroundColor: 'var(--surface-muted, rgba(79,70,229,0.05))',
            borderRadius: '8px',
            borderLeft: '3px solid var(--primary)',
          }}>
            {t(HINT_KEYS[currentKey] as any)}
          </p>

          {/* Input area */}
          {isTagStep ? (
            <div>
              {/* Tags display */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
                marginBottom: '12px',
                minHeight: '36px',
              }}>
                {(vision[currentKey] as string[]).map((tag) => (
                  <span
                    key={tag}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      backgroundColor: 'var(--primary-soft, rgba(79,70,229,0.1))',
                      borderRadius: '20px',
                      fontSize: '13px',
                    }}
                  >
                    {tag}
                    <button
                      onClick={() => removeTag(tag)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: 'var(--text-muted)',
                        padding: '0 2px',
                      }}
                    >
                      x
                    </button>
                  </span>
                ))}
              </div>

              {/* Tag input */}
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); addTag(); }
                  }}
                  placeholder={t(`careerVision.${currentKey}Placeholder` as any)}
                  style={{
                    flex: 1,
                    padding: '10px 14px',
                    border: '1px solid var(--border)',
                    borderRadius: '8px',
                    fontSize: '14px',
                    backgroundColor: 'var(--surface)',
                    color: 'var(--text)',
                  }}
                />
                <button
                  onClick={addTag}
                  disabled={!tagInput.trim()}
                  style={{
                    padding: '10px 16px',
                    backgroundColor: tagInput.trim() ? 'var(--primary)' : 'var(--border)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: tagInput.trim() ? 'pointer' : 'not-allowed',
                    fontSize: '13px',
                  }}
                >
                  {t('careerVision.addItem')}
                </button>
              </div>
            </div>
          ) : (
            <textarea
              value={vision[currentKey] as string}
              onChange={(e) => setVision({ ...vision, [currentKey]: e.target.value })}
              placeholder={t(`careerVision.${currentKey}Placeholder` as any)}
              rows={5}
              style={{
                width: '100%',
                padding: '12px 14px',
                border: '1px solid var(--border)',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: 'var(--surface)',
                color: 'var(--text)',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          )}

          {/* Talk with Alex button */}
          <button
            onClick={() => setVoiceActive(true)}
            disabled={extracting}
            style={{
              marginTop: '12px',
              padding: '8px 16px',
              backgroundColor: 'transparent',
              border: '1px solid var(--primary)',
              borderRadius: '8px',
              color: 'var(--primary)',
              cursor: extracting ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              opacity: extracting ? 0.6 : 1,
            }}
          >
            <span style={{ fontSize: '16px' }}>🎙</span>
            {extracting ? t('careerVision.extracting') : t('careerVision.talkWithAlex')}
          </button>
        </div>
      )}

      {/* Navigation buttons */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '24px',
        gap: '12px',
      }}>
        <button
          onClick={() => setStep(s => s - 1)}
          disabled={step === 0}
          style={{
            padding: '10px 20px',
            backgroundColor: 'var(--surface-muted)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            cursor: step === 0 ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            color: 'var(--text)',
            opacity: step === 0 ? 0.5 : 1,
          }}
        >
          {t('careerVision.previous')}
        </button>

        <div style={{ display: 'flex', gap: '8px' }}>
          {isLastStep ? (
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '10px 24px',
                backgroundColor: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: '600',
              }}
            >
              {saving ? t('careerVision.saving') : t('careerVision.save')}
            </button>
          ) : (
            <button
              onClick={() => setStep(s => s + 1)}
              style={{
                padding: '10px 24px',
                backgroundColor: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              {t('careerVision.next')}
            </button>
          )}
        </div>
      </div>

      {/* Status messages */}
      {saved && (
        <div style={{
          marginTop: '16px',
          padding: '10px 14px',
          backgroundColor: 'rgba(34,197,94,0.1)',
          border: '1px solid rgba(34,197,94,0.3)',
          borderRadius: '8px',
          color: '#22c55e',
          fontSize: '14px',
        }}>
          {t('careerVision.saved')}
        </div>
      )}

      {error && (
        <div style={{
          marginTop: '16px',
          padding: '10px 14px',
          backgroundColor: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: '8px',
          color: '#ef4444',
          fontSize: '14px',
        }}>
          {error}
        </div>
      )}

      {/* Voice Mode overlay */}
      {voiceActive && (
        <VoiceMode onClose={handleVoiceClose} chatHistory={voiceChatHistory} />
      )}
    </div>
  );
}
